import { build360ImageUrl } from "~/utils/images/360";
import { buildLoadingBatches } from "~/utils/images/360-batching";
import { loadImage } from "~/utils/images/loadImage";

export const SCALE_CONVERSION_FACTOR = 100;

const INITIAL_IMAGES_TO_LOAD_PER_SIDE = 4;
const WAVE_SIZE = 3; // images per side per wave
const HIGH_RES_DEBOUNCE_MS = 150;

/**
 * Composable to manage 360° image loading and preloading
 * - Loads current image first with feedback
 * - Preloads nearby frames in an expanding wave pattern
 * - Tracks per-frame loaded state and current-image error
 */
export const use360ImageLoader = (
  baseUrl: MaybeRefOrGetter<string>,
  currentImageIndex: MaybeRefOrGetter<number>,
  totalImages: MaybeRefOrGetter<number>,
) => {
  if (import.meta.server) {
    return {
      isInitialLoading: computed(() => false),
      isFullyLoaded: computed(() => false),
      isCurrentImageLoading: ref(false),
      currentImageError: ref(false),
      currentImageUrl: computed(() => ""),
      loadImageWithFeedback: async (_index: number) => {},
      ensureHighRes: async (_index: number) => {},
    };
  }

  const loadedImages = ref<Set<number>>(new Set());
  const loadedHighResImages = ref<Set<number>>(new Set());
  const initialBatchSettled = ref(false);
  const currentImageError = ref(false);
  const isCurrentImageLoading = computed(
    () =>
      !loadedImages.value.has(toValue(currentImageIndex)) &&
      !currentImageError.value,
  );
  const isInitialLoading = computed(() => !initialBatchSettled.value);

  const $img = useImage();
  const resolveUrl = (index: number, size: "750x750" | "2000x2000") => {
    const raw = build360ImageUrl(toValue(baseUrl), index, { size });

    return $img(raw, { format: "avif" });
  };

  const currentImageUrl = computed(() => {
    const index = toValue(currentImageIndex);
    const size = loadedHighResImages.value.has(index) ? "2000x2000" : "750x750";

    return resolveUrl(index, size);
  });

  const resetState = () => {
    loadedImages.value.clear();
    loadedHighResImages.value.clear();
    currentImageError.value = false;
    initialBatchSettled.value = false;
  };

  // Load one image and update current-image feedback (loading, error)
  const loadImageWithFeedback = async (index: number) => {
    if (loadedImages.value.has(index)) {
      currentImageError.value = false;

      return;
    }
    currentImageError.value = false;

    try {
      await loadImage(resolveUrl(index, "750x750"));
      loadedImages.value.add(index);
      if (index === toValue(currentImageIndex)) {
        currentImageError.value = false;
      }
    } catch {
      if (index === toValue(currentImageIndex)) {
        currentImageError.value = true;
      }
    }
  };

  const ensureHighRes = async (index: number) => {
    if (loadedHighResImages.value.has(index)) return;
    try {
      await loadImage(resolveUrl(index, "2000x2000"));
      loadedHighResImages.value.add(index);
    } catch {
      // Ignore high-res loading errors
    }
  };

  // Build loading plan: first around center, then expanding in waves
  // Single function to load all images in order
  const loadAllImages = async () => {
    const total = toValue(totalImages);
    // Ensure the current image is loaded with feedback first
    await loadImageWithFeedback(toValue(currentImageIndex));
    const batches = buildLoadingBatches(
      total,
      INITIAL_IMAGES_TO_LOAD_PER_SIDE,
      WAVE_SIZE,
    );

    // Initial batch: load concurrently (without toggling current-image feedback)
    const initialBatch = batches[0] ?? [];
    const waves = batches.length > 1 ? batches.slice(1) : [];
    await Promise.allSettled(
      initialBatch.map(async (i) => {
        if (loadedImages.value.has(i)) return;

        const url = resolveUrl(i, "750x750");

        return loadImage(url).then(() => {
          loadedImages.value.add(i);
        });
      }),
    );
    initialBatchSettled.value = true;

    // Remaining waves: process sequentially
    await waves.reduce(async (p, wave) => {
      await p;
      const pending = wave.filter((i) => !loadedImages.value.has(i));
      if (!pending.length) return;
      await Promise.allSettled(
        pending.map((i) =>
          loadImage(resolveUrl(i, "750x750")).then(() =>
            loadedImages.value.add(i),
          ),
        ),
      );
    }, Promise.resolve());
  };

  watch(
    () => toValue(baseUrl),
    (newUrl) => {
      if (newUrl) {
        resetState();
        nextTick(() => {
          loadAllImages();
        });
      }
    },
  );
  // Load low-res frame immediately on index change
  watch(
    () => toValue(currentImageIndex),
    (newIndex) => {
      if (!loadedImages.value.has(newIndex)) {
        loadImageWithFeedback(newIndex);
      }
    },
    { immediate: true },
  );

  watchDebounced(
    () => toValue(currentImageIndex),
    (newIndex) => {
      ensureHighRes(newIndex);
    },
    {
      debounce: HIGH_RES_DEBOUNCE_MS,
      // initial behavior previously scheduled after a timeout, so keep immediate true (runs after debounce)
      immediate: true,
      maxWait: HIGH_RES_DEBOUNCE_MS * 2,
    },
  );

  onMounted(() => {
    loadAllImages();
    ensureHighRes(toValue(currentImageIndex));
  });

  const isFullyLoaded = computed(
    () => loadedImages.value.size === toValue(totalImages),
  );

  return {
    isInitialLoading,
    isFullyLoaded,
    isCurrentImageLoading,
    currentImageError,
    currentImageUrl,
    loadImageWithFeedback,
    ensureHighRes,
  };
};
